import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, NavigateFunction } from 'react-router-dom';
import { app as teamsApp, pages } from '@microsoft/teams-js';
import { IPublicClientApplication } from '@azure/msal-browser';
import { initializeAuth } from './auth/authService';
import { ensureCurrentUserProvisioned } from './services/provisioningService';
import { verifyLicense, LicenseMode } from './services/licenseService';
import { dvGet } from './api/dataverseClient';
import LoadingScreen from './components/LoadingScreen';
import LicenseExpired from './components/LicenseExpired';
import ErrorScreen from './components/ErrorScreen';
import MyDashboard from './views/MyDashboard';
import Projects from './views/Projects';
import Portfolio from './views/Portfolio';
import PortfolioGantt from './views/PortfolioGantt';
import SubmitRequest from './views/SubmitRequest';
import Admin from './views/Admin';
import ChannelTab from './views/ChannelTab';
import PublicFormRenderer from './components/forms/PublicFormRenderer';
import MeetingPanel from './views/MeetingPanel';
import Import from './views/Import';
import RFPPipeline from './views/RFPPipeline';
import ProjectDetail from './views/project/ProjectDetail';
import { AppTheme } from './types';

type AppState = 'loading' | 'licenseExpired' | 'ready' | 'error';

// ── Deep-link target (Teams notification → in-app navigation) ─────────
//
// The Notification Center / Teams Activity Feed deep-links to a single
// task by id. Teams delivers the id one of three ways:
//   (1) ctx.page.subPageId    — modern TeamsJS, native Teams clients
//   (2) ctx.subEntityId       — legacy TeamsJS shape
//   (3) ?taskId=… on window.location.search — mobile / browser fallback
//       where Teams launches the webUrl directly instead of deep-linking
//       through the Teams app.
//
// We capture the id at app boot (before the router mounts), park it in
// a module variable (NOT localStorage — this is one-shot, not preference
// state), then have AppRoutes consume it on its first effect, resolve
// the project from the task GUID via Dataverse, and navigate. If the
// task can't be found (deleted / cross-tenant / wrong env), fall back
// to /my-dashboard with no error UI per spec.
let _initialDeepLinkTarget: string | null = null;
function setInitialDeepLinkTarget(id: string | null): void { _initialDeepLinkTarget = id; }
function consumeInitialDeepLinkTarget(): string | null {
  const v = _initialDeepLinkTarget;
  _initialDeepLinkTarget = null;
  return v;
}

class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  constructor(props: { children: React.ReactNode }) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, color: 'var(--danger)', fontFamily: 'var(--font-mono)', fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {this.state.error.message}{'\n\n'}{this.state.error.stack}
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [state, setState] = useState<AppState>('loading');
  const [pca, setPca] = useState<IPublicClientApplication | null>(null);
  const [theme, setTheme] = useState<AppTheme>('default');
  const [errorMsg, setErrorMsg] = useState('');
  const [licenseMode, setLicenseMode] = useState<LicenseMode>('buildtime');

  useEffect(() => {
    (async () => {
      try {
        const pcaInstance = await initializeAuth();
        teamsApp.notifyAppLoaded();
        const context = await teamsApp.getContext();
        setTheme((context.app.theme ?? 'default') as AppTheme);
        document.body.setAttribute('data-teams-theme', context.app.theme ?? 'default');

        // Capture deep-link target from Teams context or URL fallback.
        // TeamsJS v2: ctx.page.subPageId. v1 fallback: ctx.subEntityId.
        type TeamsCtxWithSubpage = typeof context & {
          page?: { subPageId?: string | null };
          subEntityId?: string | null;
        };
        const ctxAny = context as TeamsCtxWithSubpage;
        const subEntityId = ctxAny.page?.subPageId ?? ctxAny.subEntityId ?? null;
        // Deep-link target is "{kind}:{id}" (task|milestone|rfp) or a bare
        // task id (legacy). subEntityId carries it inside Teams; the webUrl
        // fallback carries it as ?focusKind=&focusId= (or legacy ?taskId=).
        const sp = new URLSearchParams(window.location.search);
        const focusKind = sp.get('focusKind');
        const focusId = sp.get('focusId');
        const taskIdFromUrl = sp.get('taskId');
        const target =
          subEntityId ??
          (focusKind && focusId ? `${focusKind}:${focusId}` : null) ??
          taskIdFromUrl;
        // Instrumentation (temporary, per fix-deep-links task): the app ALREADY
        // reads the SDK deep-link context (page.subPageId) here — this logs
        // exactly what Teams provides on a real notification click so the true
        // cause of "lands on dashboard" is observable from the Teams client
        // console. Inspect: does page.subPageId arrive, or is it null (context
        // not propagated through the activity-feed webUrl / app-already-open)?
        console.log(
          'DEEPLINK boot | page =', JSON.stringify(ctxAny.page ?? null),
          '| subPageId =', JSON.stringify(subEntityId),
          '| location =', window.location.href,
          '| urlFocus =', JSON.stringify({ focusKind, focusId, taskIdFromUrl }),
          '| resolvedTarget =', JSON.stringify(target),
        );
        if (target) setInitialDeepLinkTarget(target);
        teamsApp.registerOnThemeChangeHandler((t: string) => {
          setTheme(t as AppTheme);
          document.body.setAttribute('data-teams-theme', t);
        });
        // pca is needed by the License Required screen (so an admin can enter
        // a key), so set it before deciding licensed/unlicensed.
        setPca(pcaInstance);
        const license = await verifyLicense(pcaInstance);
        setLicenseMode(license.mode);
        if (!license.ok) {
          setState('licenseExpired');
          teamsApp.notifySuccess();
          return;
        }
        setState('ready');
        teamsApp.notifySuccess();

        // First-sign-in security-role provisioning — best-effort, non-blocking.
        // Inert (no-op) until Scott creates the "General User" role in the Maker
        // Portal; activates automatically then. Never blocks app load.
        ensureCurrentUserProvisioned(pcaInstance)
          .then(r => console.log('PROVISIONING', r.status, '—', r.detail))
          .catch(() => { /* never surfaces to the user */ });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Initialization failed';
        setErrorMsg(msg);
        setState('error');
        teamsApp.notifyFailure({ reason: teamsApp.FailedReason.Other, message: msg });
      }
    })();
  }, []);

  // Re-verify the license every 60 minutes — ONLY on the runtime (Dataverse
  // key) path. If it comes back inactive/expired, drop to the blocking License
  // Required screen (in-app; no window.alert, which Teams suppresses). The
  // build-time fallback path is unaffected by this timer.
  useEffect(() => {
    if (state !== 'ready' || licenseMode !== 'runtime' || !pca) return;
    const interval = setInterval(async () => {
      const result = await verifyLicense(pca);
      if (!result.ok) setState('licenseExpired');
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [state, licenseMode, pca]);

  if (state === 'loading')        return <LoadingScreen />;
  if (state === 'licenseExpired') return <LicenseExpired pca={pca ?? undefined} />;
  if (state === 'error')          return <ErrorScreen message={errorMsg} />;
  if (!pca)                       return <LoadingScreen />;

  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <AppRoutes pca={pca} theme={theme} />
    </BrowserRouter>
    </AppErrorBoundary>
  );
}

function AppRoutes({ pca, theme }: { pca: IPublicClientApplication; theme: AppTheme }) {
  const navigate = useNavigate();
  // Keep a live ref to navigate() so the Teams handler (registered once
  // on mount) always calls the current router. Without this, the handler
  // could capture a stale navigate from initial render.
  const navigateRef = useRef<NavigateFunction>(navigate);
  navigateRef.current = navigate;

  // App-level shell nav: this app has NO React-side top nav bar. The
  // visible "My Tasks | Projects | Portfolio | Import" rail is rendered
  // by Teams from the manifest's staticTabs entries. Each tab loads its
  // contentUrl in this iframe. The Projects tab → /portfolio. When the
  // user clicks the Projects tab while already inside /project/:id (i.e.
  // the iframe URL didn't change), Teams fires the pages focusEnter
  // event so we can complete the navigation ourselves. Returning true
  // tells Teams "I handled this".
  //
  // Defensive registration:
  //   - pages.isSupported() guards against hosts without the pages
  //     capability (Outlook, M365 web, etc.) where the call would throw.
  //   - The handler reads navigateRef.current so a re-render between
  //     registration and invocation doesn't cause a stale-closure bug.
  //   - Backup: also fallback navigate when popstate fires for the same
  //     pattern, so browser back/forward inside the iframe behave
  //     consistently with the Teams tab click.
  useEffect(() => {
    try {
      const supported = pages?.isSupported ? pages.isSupported() : false;
      if (supported && typeof pages.registerFocusEnterHandler === 'function') {
        pages.registerFocusEnterHandler(() => {
          // Instrumentation (temporary): if a notification is clicked while the
          // app is ALREADY open, the boot getContext() won't re-run — this logs
          // whether Teams surfaces the deep-link subPageId on focus-enter, which
          // pinpoints whether "lands on dashboard" is the app-already-open case.
          teamsApp.getContext().then(ctx => {
            const p = (ctx as { page?: { subPageId?: string | null; id?: string } }).page;
            console.log('DEEPLINK focusEnter | page =', JSON.stringify(p ?? null),
              '| path =', window.location.pathname);
          }).catch(() => {});
          if (window.location.pathname.startsWith('/project/')) {
            navigateRef.current('/portfolio');
            return true;
          }
          return false;
        });
      }
    } catch {
      // SDK absent or pages capability not granted in this host. The
      // breadcrumb Link inside ProjectDetail still works as the manual
      // back-navigation path.
    }
  }, []);

  // Deep-link consumer. Runs once on mount. If a target id was parked
  // during App init (Teams subEntityId / URL taskId), look the task up
  // in Dataverse, derive its project, and navigate to that project
  // with ?taskId=… so the project's TasksTab can open the task drawer.
  useEffect(() => {
    const raw = consumeInitialDeepLinkTarget();
    console.log('DEEPLINK consume | rawTarget =', JSON.stringify(raw));
    if (!raw) return;
    // Parse "{kind}:{id}"; a bare id (no colon) is a task, for back-compat
    // with deep links minted before typed kinds existed.
    const ci = raw.indexOf(':');
    const kind = ci > 0 ? raw.slice(0, ci) : 'task';
    const id = ci > 0 ? raw.slice(ci + 1) : raw;
    console.log('DEEPLINK route | kind =', kind, '| id =', id);
    (async () => {
      try {
        if (kind === 'milestone') {
          const ms = await dvGet<{ _dagrun_pm_projectid_value: string | null }>(
            pca, 'dagrun_pm_milestones', id, { select: ['_dagrun_pm_projectid_value'] },
          );
          const projectId = ms._dagrun_pm_projectid_value;
          if (projectId) {
            navigateRef.current(`/project/${projectId}`,
              { replace: true, state: { tab: 'milestones', focusKind: 'milestone', focusId: id } });
            return;
          }
        } else if (kind === 'rfp') {
          navigateRef.current('/rfp', { replace: true, state: { focusKind: 'rfp', focusId: id } });
          return;
        } else {
          // task (default / legacy). Resolve its project, open the drawer.
          const task = await dvGet<{ _dagrun_pm_projectid_value: string | null }>(
            pca, 'dagrun_pm_tasks', id, { select: ['_dagrun_pm_projectid_value'] },
          );
          const projectId = task._dagrun_pm_projectid_value;
          if (projectId) {
            navigateRef.current(`/project/${projectId}?taskId=${id}`, { replace: true });
            return;
          }
        }
        navigateRef.current('/my-dashboard', { replace: true });
      } catch {
        // Not found / unauthorised / cross-tenant — silently fall back to the
        // dashboard per spec.
        navigateRef.current('/my-dashboard', { replace: true });
      }
    })();
  }, [pca]);

  return (
    <Routes>
      <Route path="/mytasks"             element={<Navigate to="/my-dashboard" replace />} />
      <Route path="/my-dashboard"        element={<MyDashboard   pca={pca} theme={theme} />} />
      <Route path="/portfolio"           element={<Projects      pca={pca} theme={theme} />} />
      <Route path="/projects"            element={<Projects      pca={pca} theme={theme} />} />
      <Route path="/project/:projectId"  element={<ProjectDetail pca={pca} theme={theme} />} />
      <Route path="/project/:projectId/submit" element={<SubmitRequest pca={pca} theme={theme} />} />
      <Route path="/portfolio/full"      element={<Portfolio     pca={pca} theme={theme} />} />
      <Route path="/portfolio/timeline"  element={<PortfolioGantt pca={pca} theme={theme} />} />
      <Route path="/submit"              element={<SubmitRequest pca={pca} theme={theme} />} />
      <Route path="/admin"               element={<Admin         pca={pca} theme={theme} />} />
      <Route path="/import"              element={<Import        pca={pca} theme={theme} />} />
      <Route path="/rfp"                 element={<RFPPipeline   pca={pca} theme={theme} />} />
      <Route path="/channel-tab"         element={<ChannelTab    pca={pca} theme={theme} />} />
      <Route path="/forms/:formId"       element={<PublicFormRenderer pca={pca} theme={theme} />} />
      <Route path="/meeting-panel"       element={<MeetingPanel  pca={pca} theme={theme} />} />
      <Route path="*"                    element={<MyDashboard   pca={pca} theme={theme} />} />
    </Routes>
  );
}
